import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Reusable renderer for AI-generated text. The model emits real
 * markdown — bullets, headings, **bold**, tables, code fences — so
 * the UI must render it instead of dumping `\n\n**foo**` strings on
 * the page.
 *
 * Use anywhere an LLM response is displayed: Compose draft, Brief
 * body, Compare summary, Audit answer, Ask conversation, workflow
 * step outputs that look like prose.
 *
 * Styled to match the Knowlez design system (ink/accent tokens, no
 * @tailwindcss/typography dependency).
 */
const components: Components = {
  p: ({ children, ...props }) => (
    <p className="text-[14px] text-ink-900 leading-relaxed my-2 first:mt-0 last:mb-0" {...props}>
      {children}
    </p>
  ),
  h1: ({ children }) => (
    <h1 className="text-base font-semibold text-ink-900 mt-4 mb-2 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-[15px] font-semibold text-ink-900 mt-4 mb-2 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-ink-900 mt-3 mb-1.5 first:mt-0">{children}</h3>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-5 my-2 space-y-1 text-[14px] text-ink-900 leading-relaxed">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 my-2 space-y-1 text-[14px] text-ink-900 leading-relaxed">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-ink-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent-600 underline decoration-accent-300 underline-offset-2 hover:decoration-accent-500"
    >
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    const isBlock = (className ?? '').startsWith('language-');
    if (isBlock) {
      // Block fences are rendered by `pre`; just return content + class.
      return <code className={className}>{children}</code>;
    }
    return (
      <code className="rounded bg-ink-100 dark:bg-ink-800 px-1.5 py-0.5 text-[12.5px] font-mono text-ink-900 dark:text-ink-100">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-lg border border-ink-200 bg-ink-50 dark:bg-ink-900 dark:border-ink-700 px-3 py-2 text-[12.5px] font-mono leading-relaxed text-ink-900 dark:text-ink-100">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-2 border-accent-300 pl-3 text-[13.5px] italic text-ink-700 dark:text-ink-300">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="w-full text-[13px] border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="text-ink-500 dark:text-ink-400">{children}</thead>,
  th: ({ children }) => (
    <th className="border-b border-ink-200 dark:border-ink-700 px-2 py-1.5 text-left font-medium">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-b border-ink-100 dark:border-ink-800 px-2 py-1.5 align-top">{children}</td>
  ),
  hr: () => <hr className="my-4 border-t border-ink-100 dark:border-ink-700" />,
};

export function MarkdownText({ children, className }: { children: string; className?: string }) {
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
